/**
 * Offer Comments (Sticky Notes) API operations
 */
import { del, get, post } from './base';
import type { CreateCommentData, NotificationsInbox, OfferComment } from './types';

// Offer Comments API Functions (Sticky Notes)

/**
 * Get sticky note comments for an offer (authenticated recruiter)
 */
export const getOfferComments = async (offerId: string): Promise<OfferComment[]> => {
  const { items } = await get<{ items: OfferComment[] }>(`/offers/${offerId}/comments/`);
  return items ?? [];
};

/**
 * Create a sticky note comment on an offer (authenticated recruiter)
 */
export const createOfferComment = async (
  offerId: string,
  data: CreateCommentData
): Promise<OfferComment> => {
  return post<OfferComment>(`/offers/${offerId}/comments/create/`, data);
};

/**
 * Delete a comment on an offer (authenticated recruiter)
 */
export const deleteOfferComment = async (
  offerId: string,
  commentId: string
): Promise<{ id: string }> => {
  return del<{ id: string }>(`/offers/${offerId}/comments/${commentId}/delete/`);
};

/**
 * Resolve a comment thread (authenticated recruiter)
 */
export const resolveOfferComment = async (
  offerId: string,
  commentId: string
): Promise<OfferComment> => {
  return post<OfferComment>(`/offers/${offerId}/comments/${commentId}/resolve/`);
};

/**
 * Get sticky note comments for an offer using signed link (public candidate)
 */
export const getOfferCommentsWithSignature = async (
  signedData: string
): Promise<OfferComment[]> => {
  const { items } = await get<{ items: OfferComment[] }>(
    `/offers/view/${encodeURIComponent(signedData)}/comments/`
  );
  return items ?? [];
};

/**
 * Create a sticky note comment on an offer using signed link (public candidate)
 */
export const createOfferCommentWithSignature = async (
  signedData: string,
  data: CreateCommentData
): Promise<OfferComment> => {
  return post<OfferComment>(
    `/offers/view/${encodeURIComponent(signedData)}/comments/create/`,
    data
  );
};

// Notifications Inbox API Functions

/**
 * Get notifications inbox with unread comments from candidates
 */
export const getNotificationsInbox = async (): Promise<NotificationsInbox> => {
  return get<NotificationsInbox>('/notifications/inbox/');
};

/**
 * Get total unread comment count
 */
export const getUnreadCount = async (): Promise<{ total_unread: number }> => {
  return get<{ total_unread: number }>('/notifications/unread-count/');
};

/**
 * Mark comments as read
 */
export const markCommentsAsRead = async (
  commentIds: string[]
): Promise<{ marked_count: number }> => {
  return post<{ marked_count: number }>('/notifications/mark-read/', { comment_ids: commentIds });
};
